import { useUsersQuery } from "@/api/users.api";
import { Select, SelectItem } from "@/components/generic/Select";
import { TextField } from "@/components/generic/TextField";
import { MaybePromise } from "@/types/maybe-promise";
import { StepperSubmitHandler } from "@/utils/stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { SegmentHeader } from "../basic/SegmentHeader";

type CampaignNameAndResponsibleId = {
  campaignName: string;
  responsibleId: string;
};

export type CampaignDetailsFormProps = {
  onSubmit: (values: CampaignNameAndResponsibleId) => MaybePromise<void>;
  placeholder?: string;
  values?: CampaignNameAndResponsibleId;
  setSubmitHandler?: (submitHandler: StepperSubmitHandler) => void;
};

const formSchema = z.object({
  campaignName: z.string(),
  responsibleId: z.string(),
});

export function CampaignDetailsForm({
  values,
  placeholder,
  onSubmit,
  setSubmitHandler,
}: CampaignDetailsFormProps) {
  const { data: usersQueryData, isPending: isUsersPending } = useUsersQuery({});

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    reset(values);
  }, [reset, values]);

  const users = useMemo(
    () => usersQueryData?.pages.flatMap((page) => page.results),
    [usersQueryData],
  );

  const submit = useMemo(
    () => handleSubmit(onSubmit),
    [handleSubmit, onSubmit],
  );

  useEffect(() => {
    setSubmitHandler?.({
      submit,
      isDirty,
      isDisabled: !isValid || isSubmitting,
      isPending: isSubmitting,
    });
  }, [setSubmitHandler, submit, isDirty, isValid, isSubmitting]);

  return (
    <form
      className="mx-auto flex max-w-xl flex-col justify-center gap-y-10"
      onSubmit={submit}
    >
      <SegmentHeader
        className="w-full max-w-sm"
        title="Navn"
        description={`Giv din kampagne et navn som beskriver kampagnen bedst muligt, f.eks. "+5 ansatte i København"`}
      />

      <TextField
        control={control}
        name="campaignName"
        aria-label="Kampagnenavn"
        placeholder={placeholder}
        autoFocus
      />

      <SegmentHeader
        className="w-full max-w-sm"
        title="Ansvarlig"
        description="Vælg en sælger der er ansvarlig for kampagnen. Sælgeren vil blive tildelt alle leads der leveres."
      />

      <Controller
        control={control}
        name="responsibleId"
        render={({ field, fieldState }) => (
          <Select
            className="w-full"
            placeholder={
              isUsersPending ? "Indlæser brugere.." : "Vælg ansvarlig"
            }
            aria-label="Vælg ansvarlig"
            isDisabled={isUsersPending}
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            items={users?.map((user) => ({
              label: user.name,
              value: user.id,
            }))}
            errorMessage={fieldState.error?.message}
          >
            {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
          </Select>
        )}
      />
    </form>
  );
}
